import { AppWindow, Image as ImageIcon, Info, Monitor, PanelTop } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Modal } from '../components/ui';
import { desktop, type DesktopInfo, type DesktopSource } from '../lib/desktop';
import { detectPlatform, supports, type ScreenShareOptions, type ShareSurface } from '../lib/media';
import { updateSettings, useSettings } from '../lib/settings';
import type { MeetingSession } from '../meeting/MeetingSession';
import { DesktopSourcePicker } from './DesktopSourcePicker';
import { closeFloating, documentPipSupported, floatingStore, openFloating } from './floating';

const SURFACES: { id: ShareSurface; title: string; text: string; icon: ReactNode }[] = [
  { id: 'monitor', title: 'Pantalla completa', text: 'Todo lo que ves en tu monitor', icon: <Monitor size={30} /> },
  { id: 'window', title: 'Ventana', text: 'Una aplicación concreta: documento, presentación…', icon: <AppWindow size={30} /> },
  { id: 'browser', title: 'Pestaña del navegador', text: 'Una web o vídeo, con su sonido', icon: <PanelTop size={30} /> },
];

function audioHint(surface: ShareSurface): string {
  if (surface === 'browser') return 'Se compartirá el sonido de la pestaña elegida.';
  if (surface === 'window') return 'Las ventanas no pueden compartir sonido en el navegador; usa una pestaña o la pantalla completa.';
  return detectPlatform() === 'desktop' && /Mac/.test(navigator.userAgent)
    ? 'En Mac, el sonido del sistema solo se comparte en navegadores recientes (Chrome/Edge) y macOS 14.2 o posterior. Si no está disponible, comparte una pestaña.'
    : 'Se compartirá el sonido de tu equipo.';
}

function desktopAudioHint(info: DesktopInfo | null): string {
  if (!info) return '';
  if (!info.systemAudio) return info.platform === 'linux' ? 'En Linux todavía no se puede compartir el sonido del equipo.' : 'Compartir el sonido del equipo requiere macOS 13 o posterior.';
  return 'Se compartirá todo el sonido de tu equipo (no solo el de la ventana elegida). Los demás no oirán su propia voz si usan auriculares.';
}

/**
 * Zoom-style "Share" window. In a browser, the final pick of a specific screen/window/tab happens
 * in the browser's own picker (pages cannot list other windows). In the desktop app the dialog
 * itself shows live thumbnails of every screen and window.
 */
export function ShareDialog({ session, mode, onClose }: { session: MeetingSession; mode: 'new' | 'change'; onClose(): void }) {
  const settings = useSettings();
  const screenOk = supports.screenShare;
  const [tab, setTab] = useState<'screens' | 'files'>(screenOk ? 'screens' : 'files');
  const [surface, setSurface] = useState<ShareSurface>(settings.shareSurface);
  const [audio, setAudio] = useState(settings.shareAudio);
  const [optimize, setOptimize] = useState(settings.shareOptimizeVideo);
  const [floating, setFloating] = useState(settings.shareFloating);
  const fileRef = useRef<HTMLInputElement>(null);
  const [source, setSource] = useState<DesktopSource | null>(null);
  const [info, setInfo] = useState<DesktopInfo | null>(null);
  const selectSource = useCallback((s: DesktopSource) => setSource(s), []);

  useEffect(() => {
    void desktop?.info().then(setInfo, () => undefined);
  }, []);

  const shareDesktop = async (picked: DesktopSource | null) => {
    if (!picked) return;
    updateSettings({ shareAudio: audio, shareOptimizeVideo: optimize, shareFloating: floating });
    onClose();
    const opts: ScreenShareOptions = {
      surface: picked.kind === 'screen' ? 'monitor' : 'window',
      audio: audio && !!info?.systemAudio,
      optimizeVideo: optimize,
      source: { id: picked.id, name: picked.name },
    };
    const ok = mode === 'change' ? (await session.changeShareSource(opts), !!session.store.get().screenStream) : await session.startShare(opts);
    // Like Zoom: the meeting window steps aside and the floating bar and cameras take over.
    if (ok && floating && mode === 'new') void openFloating(session, { forShare: true });
  };

  const share = async () => {
    if (desktop) return shareDesktop(source);
    updateSettings({ shareSurface: surface, shareAudio: audio, shareOptimizeVideo: optimize, shareFloating: floating });
    onClose();
    // The floating window needs the click's user gesture, so it opens before the picker.
    const openedNow = floating && documentPipSupported() && !floatingStore.get().open ? await openFloating(session) : false;
    const opts = { surface, audio, optimizeVideo: optimize };
    const ok = mode === 'change' ? (await session.changeShareSource(opts), !!session.store.get().screenStream) : await session.startShare(opts);
    if (!ok && openedNow) closeFloating();
  };

  return (
    <Modal
      title={mode === 'change' ? 'Cambiar lo que compartes' : 'Compartir'}
      onClose={onClose}
      wide
      footer={
        tab === 'screens' ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={() => void share()} disabled={!screenOk || (!!desktop && !source)}>
              Compartir
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
        )
      }
    >
      <div className="segmented share-tabs" role="tablist" aria-label="Qué compartir">
        <button type="button" role="tab" aria-pressed={tab === 'screens'} aria-selected={tab === 'screens'} onClick={() => setTab('screens')}>
          Pantallas
        </button>
        <button type="button" role="tab" aria-pressed={tab === 'files'} aria-selected={tab === 'files'} onClick={() => setTab('files')}>
          Fotos e imágenes
        </button>
      </div>

      {tab === 'screens' ? (
        <div className="share-layout-dialog">
          {desktop ? (
            <DesktopSourcePicker selected={source} onSelect={selectSource} onShare={(s) => void shareDesktop(s)} />
          ) : (
          <div>
            {!screenOk && (
              <div className="callout callout-warning" style={{ marginBottom: 12 }}>
                <Info size={16} />
                <span>Este dispositivo no permite compartir la pantalla desde el navegador (Apple lo restringe en iPhone y iPad). Puedes compartir fotos e imágenes.</span>
              </div>
            )}
            <div className="share-surfaces" role="radiogroup" aria-label="Tipo de contenido">
              {SURFACES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  role="radio"
                  aria-checked={surface === s.id}
                  className="share-surface"
                  disabled={!screenOk}
                  onClick={() => setSurface(s.id)}
                  onDoubleClick={() => void share()}
                >
                  <span className="share-surface-icon">{s.icon}</span>
                  <strong>{s.title}</strong>
                  <span>{s.text}</span>
                </button>
              ))}
            </div>
            <p className="hint" style={{ margin: '12px 0 0' }}>
              Al pulsar «Compartir», el navegador te mostrará las pantallas, ventanas o pestañas disponibles para que elijas exactamente cuál.
            </p>
          </div>
          )}

          <aside className="share-options">
            <h3>Opciones para compartir</h3>
            <label className="check">
              <input type="checkbox" checked={audio && (!desktop || !info || info.systemAudio)} onChange={(e) => setAudio(e.target.checked)} disabled={!screenOk || (!!info && !info.systemAudio)} />
              Compartir sonido del equipo
            </label>
            {desktop ? (audio || (info && !info.systemAudio)) && <p className="hint">{desktopAudioHint(info)}</p> : audio && <p className="hint">{audioHint(surface)}</p>}
            <label className="check">
              <input type="checkbox" checked={optimize} onChange={(e) => setOptimize(e.target.checked)} disabled={!screenOk} />
              Optimizar para vídeo
            </label>
            <p className="hint">Más fluido para vídeos y animaciones; sin marcar, prioriza la nitidez del texto.</p>
            {desktop ? (
              <>
                <label className="check">
                  <input type="checkbox" checked={floating} onChange={(e) => setFloating(e.target.checked)} />
                  Barra de controles y cámaras flotantes
                </label>
                <p className="hint">Siempre encima de las demás aplicaciones y fuera de lo que compartes. La ventana de la reunión se minimiza mientras compartes.</p>
              </>
            ) : (
              documentPipSupported() && (
                <>
                  <label className="check">
                    <input type="checkbox" checked={floating} onChange={(e) => setFloating(e.target.checked)} disabled={!screenOk} />
                    Mostrar barra flotante con cámaras
                  </label>
                  <p className="hint">Una ventana pequeña, siempre visible y movible, con los controles y las cámaras mientras usas otras aplicaciones.</p>
                </>
              )
            )}
          </aside>
        </div>
      ) : (
        <div className="share-files">
          <ImageIcon size={40} />
          <p>Comparte una foto, captura o imagen. En iPhone y iPad puedes elegirla de Fotos, Archivos o hacer una foto.</p>
          <Button variant="primary" onClick={() => (mode === 'change' || session.canShare()) && fileRef.current?.click()}>
            Elegir foto o imagen
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            data-testid="share-image-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              onClose();
              if (mode === 'change') session.stopShare();
              void session.startImageShare(file);
            }}
          />
        </div>
      )}
    </Modal>
  );
}
